import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Product } from "@/types/services";

export const useServiceSaving = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveServices = async (products: Product[]) => {
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "You must be logged in to save services",
        });
        setIsSubmitting(false);
        return;
      }

      // Filter out products that don't have a name or price
      const validProducts = products.filter(product => product.name.trim() !== "" && product.price > 0);
      
      if (validProducts.length === 0) {
        toast({
          variant: "destructive",
          title: "No valid services",
          description: "Please add at least one service with a name and price",
        });
        setIsSubmitting(false);
        return;
      }

      try {
        const { data: bucketData, error: bucketError } = await supabase.storage.getBucket('service_images');
        if (bucketError) {
          console.log('Attempting to create service_images bucket');
          const { error: createBucketError } = await supabase.storage.createBucket('service_images', {
            public: true
          });
          
          if (createBucketError) {
            console.error('Error creating bucket:', createBucketError);
          }
        }
      } catch (bucketCheckError) {
        console.error('Error checking bucket:', bucketCheckError);
      }

      const servicesWithUrls = await Promise.all(
        validProducts.map(async (product) => {
          const imageUrls = await Promise.all(
            product.images.map(async (image) => {
              const fileName = `${crypto.randomUUID()}-${image.file.name}`;
              const filePath = `${user.id}/${fileName}`;
              
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('service_images')
                .upload(filePath, image.file);

              if (uploadError) {
                console.error('Error uploading image:', uploadError);
                throw uploadError;
              }

              const { data: { publicUrl } } = supabase.storage
                .from('service_images')
                .getPublicUrl(filePath);

              return publicUrl;
            })
          );

          const { data: service, error: serviceError } = await supabase
            .from('services')
            .insert({
              name: product.name,
              price: product.price,
              description: product.description,
              user_id: user.id,
              image_url: imageUrls[0],
              is_active: true
            })
            .select()
            .single();

          if (serviceError) throw serviceError;

          if (imageUrls.length > 1) {
            const additionalImages = imageUrls.slice(1).map((url, index) => ({
              service_id: service.id,
              image_url: url,
              sequence: index + 1
            }));

            const { error: imagesError } = await supabase
              .from('service_images')
              .insert(additionalImages);

            if (imagesError) throw imagesError;
          }

          return service;
        })
      );

      toast({
        title: "Success",
        description: "Your services have been saved successfully!",
      });

      // Call edge function to update completeness after service save
      try {
        const { getAuthToken } = await import("@/lib/auth");
        const { updateProfileCompleteness } = await import("@/hooks/useProfileSettings");
        const accessToken = await getAuthToken();
        if (user.id && accessToken) {
          updateProfileCompleteness(user.id, accessToken);
        }
      } catch (e) {
        console.error("Failed to call updateProfileCompleteness after service save:", e);
      }

      navigate('/payment-methods');
    } catch (error) {
      console.error('Error saving services:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save services. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    saveServices
  };
};
